import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Mail, MapPin, Phone, Search, UserRound } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ensureUserAccount } from '@/services/accountService';
import { getClients, getTotalMonthlyRevenue } from '@/services/clientService';
import AppShell, { PageLoading } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

/**
 * Clients Page
 * Route: /clients
 * 
 * Lists all active clients for the account (from won quotes)
 */

const FREQUENCY_LABELS = {
  one_time: 'One-Time',
  weekly: 'Weekly',
  bi_weekly: 'Bi-Weekly',
  monthly: 'Monthly',
};

function formatCurrency(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function getClientDisplayName(client) {
  return client.customer_name || client.property_address || 'Unnamed client';
}

function getClientServices(client) {
  const services = [];

  if (client.services?.primaryService) {
    services.push(client.services.primaryService);
  }

  if (Array.isArray(client.services?.addons)) {
    for (const addon of client.services.addons) {
      if (addon?.name) services.push(addon.name);
    }
  }

  return [...new Set(services.filter(Boolean))];
}

function getInitials(client) {
  const label = getClientDisplayName(client);
  const parts = label.split(' ').filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

export default function Clients() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [account, setAccount] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const result = await ensureUserAccount(user);
      setAccount(result?.account);

      if (result?.account?.id) {
        const [clientsList, revenue] = await Promise.all([
          getClients(result.account.id),
          getTotalMonthlyRevenue(result.account.id),
        ]);
        setClients(clientsList);
        setTotalRevenue(revenue);
      }
    } catch (err) {
      console.error('[Clients] Error loading data:', err);
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      loadData();
    } else if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, loadData, navigate]);

  if (authLoading || loading) {
    return <PageLoading label="Loading clients..." />;
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredClients = normalizedSearch
    ? clients.filter((client) => {
        const services = getClientServices(client).join(' ');
        const searchable = [
          client.customer_name,
          client.property_address,
          client.customer_email,
          client.customer_phone,
          client.frequency,
          FREQUENCY_LABELS[client.frequency],
          services,
        ].filter(Boolean).join(' ').toLowerCase();

        return searchable.includes(normalizedSearch);
      })
    : clients;
  const averageClientValue = clients.length > 0 ? totalRevenue / clients.length : 0;

  return (
    <AppShell
      title="Active Clients"
      description="Won quotes become clients here, with contact details and recurring revenue."
      accountName={account?.name}
      userLabel={user?.user_metadata?.full_name || user?.email}
      actions={
        <>
          <Button onClick={() => navigate('/schedule')} variant="outline">
            Service Calendar
          </Button>
          <Button onClick={() => navigate('/quote')} className="bg-emerald-700 hover:bg-emerald-800">
            New Quote
          </Button>
        </>
      }
    >
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500">Active Clients</p>
              <p className="text-3xl font-semibold text-slate-950">{clients.length}</p>
              <p className="mt-1 text-xs text-slate-500">Won quotes now in service</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500">Monthly Revenue</p>
              <p className="text-3xl font-semibold text-slate-950">${formatCurrency(totalRevenue)}</p>
              <p className="mt-1 text-xs text-slate-500">Estimated recurring value</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500">Avg. Client Value</p>
              <p className="text-3xl font-semibold text-slate-950">${formatCurrency(averageClientValue)}</p>
              <p className="mt-1 text-xs text-slate-500">Per active client per month</p>
            </CardContent>
          </Card>
        </div>

        <div className="mb-5 rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-950">Client Directory</p>
              <p className="text-sm text-slate-500">
                Showing {filteredClients.length} of {clients.length} client{clients.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="relative w-full lg:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search name, address, email, phone, service..."
                aria-label="Search clients"
                className="pl-9"
              />
            </div>
          </div>
        </div>

        {/* Clients List */}
        {clients.length === 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 mb-4">No clients yet</p>
              <p className="text-sm text-gray-400 mb-4">
                Close quotes as &quot;Won&quot; to create clients
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={() => navigate('/quotes/pending')}
                  variant="outline"
                >
                  View Pending Quotes
                </Button>
                <Button
                  onClick={() => navigate('/quote')}
                  className="bg-emerald-700 hover:bg-emerald-800"
                >
                  Create Quote
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : filteredClients.length === 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-10 text-center">
              <p className="text-gray-500">No clients match your search.</p>
              <Button type="button" variant="outline" className="mt-4" onClick={() => setSearchTerm('')}>
                Clear Search
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {filteredClients.map((client) => (
              <Card
                key={client.id}
                className="cursor-pointer border-slate-200 shadow-sm transition-shadow hover:border-emerald-200 hover:shadow-md"
                onClick={() => navigate(`/clients/${client.id}`)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-sm font-semibold text-emerald-800">
                      {getInitials(client)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-base font-semibold text-slate-950">
                            {getClientDisplayName(client)}
                          </p>
                          {client.property_address && (
                            <p className="mt-1 flex items-start gap-1.5 text-sm text-slate-500">
                              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{client.property_address}</span>
                            </p>
                          )}
                        </div>
                        <Badge variant="secondary" className="w-fit bg-emerald-50 text-emerald-800">
                          {FREQUENCY_LABELS[client.frequency] || client.frequency || 'No frequency'}
                        </Badge>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-md bg-slate-50 p-3">
                          <p className="text-xs text-slate-500">Per Visit</p>
                          <p className="font-semibold text-slate-950">${formatCurrency(client.price_per_visit)}</p>
                        </div>
                        <div className="rounded-md bg-slate-50 p-3">
                          <p className="text-xs text-slate-500">Monthly</p>
                          <p className="font-semibold text-emerald-700">${formatCurrency(client.estimated_monthly_revenue)}</p>
                        </div>
                      </div>
                      
                      {getClientServices(client).length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {getClientServices(client).slice(0, 4).map((service) => (
                            <Badge key={service} variant="secondary" className="bg-slate-100 text-slate-700">
                              {service}
                            </Badge>
                          ))}
                          {getClientServices(client).length > 4 && (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                              +{getClientServices(client).length - 4}
                            </Badge>
                          )}
                        </div>
                      )}

                      <div className="mt-4 space-y-1.5 text-sm text-slate-500">
                        {client.customer_email && (
                          <p className="flex items-center gap-2 truncate">
                            <Mail className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{client.customer_email}</span>
                          </p>
                        )}
                        {client.customer_phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="h-3.5 w-3.5 shrink-0" />
                            {client.customer_phone}
                          </p>
                        )}
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="bg-emerald-700 hover:bg-emerald-800"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/clients/${client.id}`);
                          }}
                        >
                          <UserRound className="mr-2 h-4 w-4" />
                          Profile
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate('/schedule');
                          }}
                        >
                          <CalendarDays className="mr-2 h-4 w-4" />
                          Schedule
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </AppShell>
  );
}
